import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Inbox,
  CheckCircle2,
  XCircle,
  Clock,
  RefreshCw,
  Check,
  X,
} from "lucide-react";

import {
  fetchAccessRequests,
  updateAccessRequestStatus,
} from "../../services/accessRequestsAdminService";

function fmtDate(ms) {
  if (!ms) return "—";
  return new Date(ms).toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function StatusBadge({ status, darkMode }) {
  const map = {
    new: {
      label: "Nuova",
      classes: darkMode
        ? "bg-amber-900/30 text-amber-300"
        : "bg-amber-100 text-amber-700",
    },
    approved: {
      label: "Approvata",
      classes: darkMode
        ? "bg-green-900/30 text-green-300"
        : "bg-green-100 text-green-700",
    },
    rejected: {
      label: "Rifiutata",
      classes: darkMode
        ? "bg-red-900/30 text-red-300"
        : "bg-red-100 text-red-700",
    },
  };
  const v = map[status] || map.new;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${v.classes}`}
    >
      {v.label}
    </span>
  );
}

function StatCard({ icon: Icon, label, value, darkMode, accent }) {
  return (
    <div
      className={`rounded-xl border p-4 ${
        darkMode
          ? "bg-neutral-900 border-neutral-800"
          : "bg-white border-neutral-200"
      }`}
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon
          className={`w-4 h-4 ${accent || (darkMode ? "text-neutral-400" : "text-neutral-500")}`}
        />
        <span
          className={`text-xs font-medium ${
            darkMode ? "text-neutral-400" : "text-neutral-500"
          }`}
        >
          {label}
        </span>
      </div>
      <div
        className={`text-2xl font-semibold ${
          darkMode ? "text-neutral-100" : "text-neutral-900"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function BreakdownList({ title, entries, darkMode }) {
  if (!entries.length) return null;
  return (
    <div
      className={`rounded-xl border p-4 ${
        darkMode
          ? "bg-neutral-900 border-neutral-800"
          : "bg-white border-neutral-200"
      }`}
    >
      <h4
        className={`text-xs font-semibold uppercase tracking-wide mb-2 ${
          darkMode ? "text-neutral-400" : "text-neutral-500"
        }`}
      >
        {title}
      </h4>
      <ul className="space-y-1.5">
        {entries.map(([key, count]) => (
          <li
            key={key}
            className={`flex items-center justify-between text-sm ${
              darkMode ? "text-neutral-300" : "text-neutral-700"
            }`}
          >
            <span className="truncate">{key}</span>
            <span className="font-medium tabular-nums">{count}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ActionButtons({ req, busyId, onApprove, onReject, darkMode }) {
  const isBusy = busyId === req.id;
  if (req.status !== "new") {
    return (
      <span
        className={`text-xs ${darkMode ? "text-neutral-500" : "text-neutral-400"}`}
      >
        già gestita
      </span>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        disabled={isBusy}
        onClick={() => onApprove(req.id)}
        className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
          isBusy
            ? darkMode
              ? "bg-neutral-800 text-neutral-500"
              : "bg-neutral-200 text-neutral-400"
            : darkMode
              ? "bg-green-900/30 text-green-300 hover:bg-green-900/50"
              : "bg-green-50 text-green-700 hover:bg-green-100"
        }`}
      >
        <Check className="w-3.5 h-3.5" />
        Approva
      </button>
      <button
        type="button"
        disabled={isBusy}
        onClick={() => onReject(req.id)}
        className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
          isBusy
            ? darkMode
              ? "bg-neutral-800 text-neutral-500"
              : "bg-neutral-200 text-neutral-400"
            : darkMode
              ? "bg-red-900/30 text-red-300 hover:bg-red-900/50"
              : "bg-red-50 text-red-700 hover:bg-red-100"
        }`}
      >
        <X className="w-3.5 h-3.5" />
        Rifiuta
      </button>
    </div>
  );
}

export default function AccessRequestsSection({ darkMode }) {
  const [requests, setRequests] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState(""); // YYYY-MM-DD

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAccessRequests();
      setRequests(data);
    } catch (err) {
      console.error("AccessRequestsSection fetch error:", err);
      setError(err?.message || "Errore nel caricamento delle richieste.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleUpdateStatus = useCallback(
    async (id, status) => {
      setBusyId(id);
      setActionError(null);
      // Optimistic update
      const prev = requests;
      setRequests((list) =>
        (list || []).map((r) => (r.id === id ? { ...r, status } : r))
      );
      try {
        // L'email serve a creare approvedUsers/{email}: approvare deve aprire
        // la porta, non solo far partire il messaggio.
        const richiesta = (prev || []).find((r) => r.id === id);
        await updateAccessRequestStatus(id, status, richiesta?.email);
      } catch (err) {
        console.error("updateAccessRequestStatus error:", err);
        setRequests(prev); // rollback
        setActionError(
          err?.code === "permission-denied"
            ? "Permessi insufficienti: la regola Firestore per l'update di accessRequests non è ancora abilitata per gli admin."
            : err?.message || "Errore durante l'aggiornamento dello status."
        );
      } finally {
        setBusyId(null);
      }
    },
    [requests]
  );

  const filtered = useMemo(() => {
    const list = requests || [];
    return list.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (dateFilter) {
        if (!r.createdAtMs) return false;
        const d = new Date(r.createdAtMs).toISOString().slice(0, 10);
        if (d !== dateFilter) return false;
      }
      return true;
    });
  }, [requests, statusFilter, dateFilter]);

  const stats = useMemo(() => {
    const list = requests || [];
    const total = list.length;
    const approved = list.filter((r) => r.status === "approved").length;
    const rejected = list.filter((r) => r.status === "rejected").length;
    const pending = list.filter((r) => r.status === "new").length;

    const countBy = (field) => {
      const map = new Map();
      list.forEach((r) => {
        const key = r[field] || "Non specificato";
        map.set(key, (map.get(key) || 0) + 1);
      });
      return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
    };

    return {
      total,
      approved,
      rejected,
      pending,
      byProfessione: countBy("professione"),
      byFonte: countBy("fonte"),
    };
  }, [requests]);

  const showSkeleton = loading && !requests;
  const showEmpty = !loading && requests && filtered.length === 0;

  return (
    <div className="mt-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h2
          className={`font-display text-lg font-semibold ${
            darkMode ? "text-neutral-100" : "text-neutral-900"
          }`}
        >
          Richieste di accesso
        </h2>
        <button
          onClick={load}
          disabled={loading}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            loading
              ? darkMode
                ? "bg-neutral-800 text-neutral-500"
                : "bg-neutral-200 text-neutral-400"
              : darkMode
                ? "bg-forfy-500/15 text-forfy-300 hover:bg-forfy-500/25"
                : "bg-forfy-50 text-forfy-700 hover:bg-forfy-100"
          }`}
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Aggiorna
        </button>
      </div>

      {error && (
        <div
          className={`mb-3 p-3 rounded-lg text-sm ${
            darkMode ? "bg-red-900/30 text-red-200" : "bg-red-50 text-red-700"
          }`}
        >
          {error}
        </div>
      )}

      {actionError && (
        <div
          className={`mb-3 p-3 rounded-lg text-sm ${
            darkMode ? "bg-red-900/30 text-red-200" : "bg-red-50 text-red-700"
          }`}
        >
          {actionError}
        </div>
      )}

      {/* Statistiche aggregate */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        <StatCard
          icon={Inbox}
          label="Totale richieste"
          value={stats.total}
          darkMode={darkMode}
        />
        <StatCard
          icon={Clock}
          label="In attesa"
          value={stats.pending}
          darkMode={darkMode}
          accent={darkMode ? "text-amber-400" : "text-amber-500"}
        />
        <StatCard
          icon={CheckCircle2}
          label="Approvate"
          value={stats.approved}
          darkMode={darkMode}
          accent={darkMode ? "text-green-400" : "text-green-600"}
        />
        <StatCard
          icon={XCircle}
          label="Rifiutate"
          value={stats.rejected}
          darkMode={darkMode}
          accent={darkMode ? "text-red-400" : "text-red-500"}
        />
      </div>

      {/* Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <BreakdownList
          title="Per professione"
          entries={stats.byProfessione}
          darkMode={darkMode}
        />
        <BreakdownList
          title="Per fonte"
          entries={stats.byFonte}
          darkMode={darkMode}
        />
      </div>

      {/* Filtri */}
      <div
        className={`flex flex-col sm:flex-row gap-2 sm:items-center mb-3 p-3 rounded-xl border ${
          darkMode
            ? "bg-neutral-900 border-neutral-800"
            : "bg-white border-neutral-200"
        }`}
      >
        <label
          className={`text-xs font-medium ${
            darkMode ? "text-neutral-400" : "text-neutral-500"
          }`}
        >
          Status
        </label>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className={`px-2.5 py-1.5 rounded-lg text-sm border outline-none ${
            darkMode
              ? "bg-neutral-800 border-neutral-700 text-neutral-200"
              : "bg-neutral-50 border-neutral-200 text-neutral-700"
          }`}
        >
          <option value="all">Tutte</option>
          <option value="new">Nuove</option>
          <option value="approved">Approvate</option>
          <option value="rejected">Rifiutate</option>
        </select>

        <label
          className={`text-xs font-medium sm:ml-3 ${
            darkMode ? "text-neutral-400" : "text-neutral-500"
          }`}
        >
          Data
        </label>
        <input
          type="date"
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
          className={`px-2.5 py-1.5 rounded-lg text-sm border outline-none ${
            darkMode
              ? "bg-neutral-800 border-neutral-700 text-neutral-200"
              : "bg-neutral-50 border-neutral-200 text-neutral-700"
          }`}
        />
        {dateFilter && (
          <button
            type="button"
            onClick={() => setDateFilter("")}
            className={`text-xs underline ${
              darkMode ? "text-neutral-400" : "text-neutral-500"
            }`}
          >
            Rimuovi filtro data
          </button>
        )}

        <span
          className={`sm:ml-auto text-xs ${
            darkMode ? "text-neutral-500" : "text-neutral-400"
          }`}
        >
          {filtered.length} risultat{filtered.length === 1 ? "o" : "i"}
        </span>
      </div>

      {/* Contenuto: tabella desktop / card mobile */}
      <div
        className={`rounded-xl border overflow-hidden ${
          darkMode
            ? "bg-neutral-900 border-neutral-800"
            : "bg-white border-neutral-200"
        }`}
      >
        {showSkeleton && (
          <div className="p-4 space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className={`h-10 rounded animate-pulse ${
                  darkMode ? "bg-neutral-800" : "bg-neutral-200"
                }`}
              />
            ))}
          </div>
        )}

        {showEmpty && (
          <div
            className={`p-10 text-center text-sm ${
              darkMode ? "text-neutral-400" : "text-neutral-500"
            }`}
          >
            Nessuna richiesta trovata
          </div>
        )}

        {!showSkeleton && filtered.length > 0 && (
          <>
            {/* Tabella - desktop/tablet (>= sm) */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead
                  className={`${
                    darkMode ? "bg-neutral-900/50" : "bg-neutral-50"
                  } border-b ${
                    darkMode ? "border-neutral-800" : "border-neutral-200"
                  }`}
                >
                  <tr>
                    {["Nome", "Email", "Professione", "Data", "Status", ""].map(
                      (h) => (
                        <th
                          key={h}
                          className={`px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide ${
                            darkMode ? "text-neutral-400" : "text-neutral-500"
                          }`}
                        >
                          {h}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr
                      key={r.id}
                      className={`border-b last:border-0 ${
                        darkMode
                          ? "border-neutral-800 hover:bg-neutral-800/50"
                          : "border-neutral-100 hover:bg-neutral-50"
                      }`}
                    >
                      <td
                        className={`px-3 py-2 ${
                          darkMode ? "text-neutral-200" : "text-neutral-800"
                        }`}
                      >
                        {r.nome || "—"}
                      </td>
                      <td
                        className={`px-3 py-2 font-mono text-xs ${
                          darkMode ? "text-neutral-300" : "text-neutral-700"
                        }`}
                      >
                        {r.email || "—"}
                      </td>
                      <td
                        className={`px-3 py-2 ${
                          darkMode ? "text-neutral-300" : "text-neutral-700"
                        }`}
                      >
                        {r.professione || "—"}
                      </td>
                      <td
                        className={`px-3 py-2 ${
                          darkMode ? "text-neutral-300" : "text-neutral-700"
                        }`}
                      >
                        {fmtDate(r.createdAtMs)}
                      </td>
                      <td className="px-3 py-2">
                        <StatusBadge status={r.status} darkMode={darkMode} />
                      </td>
                      <td className="px-3 py-2">
                        <ActionButtons
                          req={r}
                          busyId={busyId}
                          onApprove={(id) => handleUpdateStatus(id, "approved")}
                          onReject={(id) => handleUpdateStatus(id, "rejected")}
                          darkMode={darkMode}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Card list - mobile (< sm) */}
            <div className="sm:hidden divide-y divide-neutral-200 dark:divide-neutral-800">
              {filtered.map((r) => (
                <div
                  key={r.id}
                  className={`p-3 ${
                    darkMode ? "border-neutral-800" : "border-neutral-100"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <span
                      className={`font-medium text-sm ${
                        darkMode ? "text-neutral-100" : "text-neutral-900"
                      }`}
                    >
                      {r.nome || "—"}
                    </span>
                    <StatusBadge status={r.status} darkMode={darkMode} />
                  </div>
                  <div
                    className={`text-xs font-mono mb-1 break-all ${
                      darkMode ? "text-neutral-400" : "text-neutral-500"
                    }`}
                  >
                    {r.email || "—"}
                  </div>
                  <div
                    className={`text-xs mb-2 ${
                      darkMode ? "text-neutral-400" : "text-neutral-500"
                    }`}
                  >
                    {r.professione || "—"} · {fmtDate(r.createdAtMs)}
                  </div>
                  <ActionButtons
                    req={r}
                    busyId={busyId}
                    onApprove={(id) => handleUpdateStatus(id, "approved")}
                    onReject={(id) => handleUpdateStatus(id, "rejected")}
                    darkMode={darkMode}
                  />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
